"use client";

import { Command } from "cmdk";
import { type ComponentProps, useEffect, useRef } from "react";

import cn from "../../utils/cn.util";
import { MenuItem, menuItemContent, type MenuItemContentProps, type MenuItemProps } from "../menu-item";
import { useComboboxContext } from "./context";

export type ComboboxItemProps = Omit<ComponentProps<typeof Command.Item>, "children"> &
  Pick<MenuItemProps, "leading" | "variant" | "trailing"> &
  MenuItemContentProps & {
    // `"indeterminate"` marks a checkbox row part picked.
    selected: boolean | "indeterminate";
  };

const Item = (allProps: ComboboxItemProps) => {
  const {
    leading,
    ref,
    className,
    label,
    description,
    children,
    trailing,
    onSelect,
    disabled,
    selected,
    variant,
    ...props
  } = allProps;
  const { setOpen, multiple, reportSelectedItem } = useComboboxContext();
  const itemRef = useRef<HTMLDivElement>(null);

  // Announce the selected item so Root can start the keyboard cursor on it when the dropdown opens.
  // The token comes from the data-value attribute cmdk stamps in a layout effect (the `value` prop, or
  // the trimmed text content when no value is given) — read here, after that stamp, rather than
  // re-deriving cmdk's fallback chain.
  useEffect(() => {
    if (selected !== true) {
      return;
    }
    const token = itemRef.current?.getAttribute("data-value");
    if (token != null) {
      reportSelectedItem(token);
    }
  }, [selected, reportSelectedItem]);

  return (
    <Command.Item
      ref={node => {
        itemRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}
      {...props}
      disabled={disabled}
      onSelect={currentValue => {
        onSelect?.(currentValue);
        if (!multiple) {
          setOpen(false);
        }
      }}
      onKeyDown={event => {
        if (event.key === "Enter" && onSelect) {
          event.stopPropagation();
          onSelect?.(props?.value ?? "");
          if (!multiple) {
            setOpen(false);
          }
        }
        if (event.key === "ArrowDown") {
          (event?.currentTarget?.nextSibling as HTMLElement)?.focus();
        }
        if (event.key === "ArrowUp") {
          (event?.currentTarget?.previousSibling as HTMLElement)?.focus();
        }
      }}
      asChild
    >
      <MenuItem
        leading={leading}
        {...menuItemContent(label, description, children)}
        variant={variant}
        trailing={trailing}
        checked={selected}
        className={cn("relative justify-between", className)}
      />
    </Command.Item>
  );
};

Item.displayName = Command.Item.displayName;

export default Item;
